import type { SelectionDecision, WatchPart } from '../api/client'

export const partOrder = ['case', 'movement', 'dial', 'hands', 'strap', 'crown', 'bracelet']

export const sortParts = (parts: Record<string, WatchPart>): Array<[string, WatchPart]> =>
  Object.entries(parts).sort(([left], [right]) => {
    const leftIndex = partOrder.indexOf(left)
    const rightIndex = partOrder.indexOf(right)

    if (leftIndex === -1 && rightIndex === -1) return left.localeCompare(right)
    if (leftIndex === -1) return 1
    if (rightIndex === -1) return -1
    return leftIndex - rightIndex
  })

export const decisionsByType = (decisions: SelectionDecision[]): Record<string, SelectionDecision> =>
  decisions.reduce<Record<string, SelectionDecision>>((lookup, decision) => {
    lookup[decision.part_type] = decision
    return lookup
  }, {})

export const formatCurrency = (price: number, currency: string): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 0,
    maximumFractionDigits: price % 1 === 0 ? 0 : 2,
  }).format(price)

const formatCrownPosition = (hour: number) => `${hour} o'clock`

const crownPositionForPart = (part: WatchPart, partType: string): number | null => {
  const caseGeometry = part.metadata?.case_geometry as { crown_position?: number } | undefined
  const movementCapabilities = part.metadata?.movement_capabilities as
    | { crown_position?: number }
    | undefined

  if (partType === 'case' && typeof caseGeometry?.crown_position === 'number') {
    return caseGeometry.crown_position
  }
  if (partType === 'movement' && typeof movementCapabilities?.crown_position === 'number') {
    return movementCapabilities.crown_position
  }
  return null
}

export const crownMismatch = (parts: Array<[string, WatchPart]>): string | null => {
  const casePart = parts.find(([partType]) => partType === 'case')
  const movementPart = parts.find(([partType]) => partType === 'movement')
  if (!casePart || !movementPart) {
    return null
  }

  const caseCrown = crownPositionForPart(casePart[1], casePart[0])
  const movementCrown = crownPositionForPart(movementPart[1], movementPart[0])
  if (caseCrown === null || movementCrown === null || caseCrown === movementCrown) {
    return null
  }

  return `Case crown is at ${formatCrownPosition(caseCrown)} but movement requires ${formatCrownPosition(movementCrown)}. Pick a matching case or movement.`
}

export const CORE_PART_COUNT = 5
